/**
 * @page BlindStructureEditorPage
 * @description 盲注結構編輯器 — CRUD 盲注結構 + Level 明細
 * 支援建立/編輯/刪除盲注結構，每個結構含多個 Level（SB/BB/Ante/Duration/Break）
 */

import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { IconArrowDown, IconArrowUp, IconChevronRight, IconX } from '@tabler/icons-react';
import { api } from '../../lib/api.ts';
import type { BlindStructureDto, CreateBlindStructureDto } from '@pokerroom/types';
import { CatalogPackageWorkspace } from './TournamentTemplatesPage';

interface LevelForm {
  levelNo: number;
  isBreak: boolean;
  smallBlind: number;
  bigBlind: number;
  ante: number;
  bba: number;
  durationSecs: number | null;
}

type Translate = (key: string, options?: Record<string, unknown>) => string;

/** Format seconds into a translated duration: exact minutes → "5分", otherwise → "2分30秒" */
export function formatDurationLabel(secs: number, t: Translate): string {
  const minutes = Math.floor(secs / 60);
  const seconds = secs % 60;
  if (seconds === 0) return t('durationMinutes', { minutes });
  return minutes > 0
    ? t('durationMinutesSeconds', { minutes, seconds })
    : t('durationSeconds', { seconds });
}

const ACTION =
  'relative inline-flex min-h-11 items-center justify-center gap-1 px-4 text-sm font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-50';
const PRIMARY = `${ACTION} bg-primary text-primary-content hover:bg-primary/90`;
const OUTLINE = `${ACTION} border border-base-300 text-base-content hover:bg-base-300`;
const DANGER_OUTLINE = `${ACTION} border border-error text-error hover:bg-error/10`;
const FIELD = 'h-11 w-full border border-base-300 bg-base-100 px-3 text-base text-base-content';
const LEVEL_FIELD =
  'h-11 w-24 border border-base-300 bg-base-100 px-2 text-right text-base tabular-nums text-base-content';
const ICON_BUTTON =
  'inline-flex h-11 w-11 items-center justify-center border border-base-300 text-base-content hover:bg-base-300 disabled:cursor-not-allowed disabled:opacity-50';
const NUM = 'px-2 py-2 text-right tabular-nums';
const BADGE = 'inline-flex h-6 items-center px-2 text-xs font-medium';

const DEFAULT_LEVEL: Omit<LevelForm, 'levelNo'> = {
  isBreak: false,
  smallBlind: 100,
  bigBlind: 200,
  ante: 0,
  bba: 0,
  durationSecs: null,
};

export default function BlindStructureEditorPage() {
  const { t } = useTranslation('tournament');
  const { t: tc } = useTranslation('common');
  const queryClient = useQueryClient();

  const [editing, setEditing] = useState<string | 'new' | null>(null);
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const [formName, setFormName] = useState('');
  const [formLevelSecs, setFormLevelSecs] = useState(900);
  const [formLevels, setFormLevels] = useState<LevelForm[]>([]);
  const [formError, setFormError] = useState('');

  const [notice, setNotice] = useState('');

  const {
    data: structures = [],
    isLoading,
    isError,
    refetch,
  } = useQuery<BlindStructureDto[]>({
    queryKey: ['blind-structures'],
    queryFn: () => api.get<BlindStructureDto[]>('/tournament/blind-structures'),
  });

  const createMutation = useMutation({
    mutationFn: (dto: CreateBlindStructureDto) => api.post('/tournament/blind-structures', dto),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['blind-structures'] });
      setEditing(null);
    },
    onError: (err) => setFormError(err instanceof Error ? err.message : t('createStructureFailed')),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, dto }: { id: string; dto: CreateBlindStructureDto }) =>
      api.patch(`/tournament/blind-structures/${id}`, dto),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['blind-structures'] });
      setEditing(null);
    },
    onError: (err) => setFormError(err instanceof Error ? err.message : t('updateStructureFailed')),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/tournament/blind-structures/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['blind-structures'] });
    },
    onError: (err) => setNotice(err instanceof Error ? err.message : t('deleteStructureFailed')),
  });

  function startCreate() {
    setEditing('new');
    setFormName('');
    setFormLevelSecs(900);
    setFormLevels(generateDefaultLevels());
    setFormError('');
  }

  function startEdit(bs: BlindStructureDto) {
    setEditing(bs.id);
    setFormName(bs.name);
    setFormLevelSecs(bs.levelSecs);
    setFormLevels(
      bs.levels.map((l) => ({
        levelNo: l.levelNo,
        isBreak: l.isBreak,
        smallBlind: l.smallBlind,
        bigBlind: l.bigBlind,
        ante: l.ante,
        bba: l.bba,
        durationSecs: l.durationSecs,
      })),
    );
    setFormError('');
  }

  function handleSave() {
    if (!formName.trim()) {
      setFormError(t('structureNameRequired'));
      return;
    }
    if (formLevels.length === 0) {
      setFormError(t('atLeastOneLevel'));
      return;
    }

    const dto: CreateBlindStructureDto = {
      name: formName.trim(),
      levelSecs: formLevelSecs,
      levels: formLevels.map((l) => ({
        levelNo: l.levelNo,
        isBreak: l.isBreak,
        smallBlind: l.smallBlind,
        bigBlind: l.bigBlind,
        ante: l.ante,
        bba: l.bba,
        durationSecs: l.durationSecs,
      })),
    };

    if (editing === 'new') {
      createMutation.mutate(dto);
    } else if (editing) {
      updateMutation.mutate({ id: editing, dto });
    }
  }

  function addLevel() {
    const lastLevel = formLevels.filter((l) => !l.isBreak).pop();
    const nextNo = formLevels.length > 0 ? Math.max(...formLevels.map((l) => l.levelNo)) + 1 : 1;
    setFormLevels([
      ...formLevels,
      {
        levelNo: nextNo,
        isBreak: false,
        smallBlind: lastLevel ? lastLevel.smallBlind * 2 : 100,
        bigBlind: lastLevel ? lastLevel.bigBlind * 2 : 200,
        ante: lastLevel ? (lastLevel.ante > 0 ? lastLevel.ante * 2 : 0) : 0,
        bba: 0,
        durationSecs: null,
      },
    ]);
  }

  function addBreak() {
    const nextNo = formLevels.length > 0 ? Math.max(...formLevels.map((l) => l.levelNo)) + 1 : 1;
    setFormLevels([
      ...formLevels,
      {
        levelNo: nextNo,
        isBreak: true,
        smallBlind: 0,
        bigBlind: 0,
        ante: 0,
        bba: 0,
        durationSecs: 600,
      },
    ]);
  }

  function removeLevel(index: number) {
    const updated = formLevels.filter((_, i) => i !== index);
    // Renumber
    setFormLevels(updated.map((l, i) => ({ ...l, levelNo: i + 1 })));
  }

  function updateLevel(index: number, field: keyof LevelForm, value: number | boolean | null) {
    setFormLevels((prev) => prev.map((l, i) => (i === index ? { ...l, [field]: value } : l)));
  }

  function moveLevel(index: number, direction: 'up' | 'down') {
    const target = direction === 'up' ? index - 1 : index + 1;
    if (target < 0 || target >= formLevels.length) return;
    const updated = [...formLevels];
    const a = updated[index]!;
    const b2 = updated[target]!;
    updated[index] = b2;
    updated[target] = a;
    setFormLevels(updated.map((l, i) => ({ ...l, levelNo: i + 1 })));
  }

  const isSaving = createMutation.isPending || updateMutation.isPending;

  async function exportStructure(bs: BlindStructureDto) {
    setNotice('');
    try {
      const data = await api.get(`/tournament/blind-structures/${bs.id}/export`);
      const blob = new Blob([JSON.stringify(data, null, 2)], {
        type: 'application/json',
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `blind-${bs.name.replace(/[^a-zA-Z0-9]/g, '_')}.json`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setNotice(err instanceof Error ? err.message : t('exportJsonFailed'));
    }
  }

  if (isLoading) {
    return (
      <p role="status" className="p-4 sm:p-6 text-base-content/70">
        {tc('loading')}
      </p>
    );
  }

  return (
    <div className="p-4 sm:p-6 max-w-5xl mx-auto">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-base-content">{t('blindStructureManagement')}</h1>
          <p className="text-sm text-base-content/70">{t('blindStructureDesc')}</p>
        </div>
        {!editing && (
          <div className="flex flex-wrap gap-2">
            <label
              className={`${OUTLINE} cursor-pointer focus-within:outline focus-within:outline-2 focus-within:outline-primary`}
            >
              {t('importJson')}
              <input
                type="file"
                accept=".json"
                className="sr-only"
                onChange={async (e) => {
                  const file = e.target.files?.[0];
                  if (!file) return;
                  setNotice('');
                  try {
                    const text = await file.text();
                    const data = JSON.parse(text);
                    await api.post('/tournament/blind-structures/import', data);
                    void queryClient.invalidateQueries({ queryKey: ['blind-structures'] });
                  } catch (err) {
                    setNotice(err instanceof Error ? err.message : t('importJsonFailed'));
                  }
                  e.target.value = '';
                }}
              />
            </label>
            <button type="button" onClick={startCreate} className={PRIMARY}>
              {t('createNewStructure')}
            </button>
          </div>
        )}
      </div>

      {notice && (
        <p role="alert" className="mb-4 border border-error p-3 text-sm text-error">
          {notice}
        </p>
      )}

      {/* 編輯 / 建立表單 */}
      {editing && (
        <section
          aria-labelledby="blind-structure-form-title"
          className="bg-base-200 border border-base-300 p-4 sm:p-6 mb-6"
        >
          <h2 id="blind-structure-form-title" className="text-lg font-bold text-base-content mb-4">
            {editing === 'new' ? t('createBlindStructure') : t('editBlindStructure')}
          </h2>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
            <div>
              <label
                htmlFor="blind-structure-name"
                className="block text-sm text-base-content mb-1"
              >
                {t('structureName')} *
              </label>
              <input
                id="blind-structure-name"
                value={formName}
                onChange={(e) => setFormName(e.target.value)}
                className={FIELD}
                placeholder={t('blindStructurePlaceholder')}
              />
            </div>
            <div>
              <label
                htmlFor="blind-structure-level-secs"
                className="block text-sm text-base-content mb-1"
              >
                {t('defaultLevelDuration')}
              </label>
              <input
                id="blind-structure-level-secs"
                type="number"
                value={formLevelSecs}
                onChange={(e) => setFormLevelSecs(parseInt(e.target.value, 10) || 900)}
                aria-describedby="blind-structure-level-secs-hint"
                className={`${FIELD} text-right tabular-nums`}
                min={60}
                step={60}
              />
              <p id="blind-structure-level-secs-hint" className="text-sm text-base-content/70 mt-1">
                {formatDurationLabel(formLevelSecs, t)}
              </p>
            </div>
          </div>

          <LevelEditorTable
            levels={formLevels}
            onChange={updateLevel}
            onMove={moveLevel}
            onRemove={removeLevel}
          />

          <div className="flex flex-wrap gap-2 mb-4">
            <button type="button" onClick={addLevel} className={OUTLINE}>
              {t('addLevel')}
            </button>
            <button type="button" onClick={addBreak} className={OUTLINE}>
              {t('addBreak')}
            </button>
          </div>

          {formError && (
            <p role="alert" className="text-sm text-error mb-3">
              {formError}
            </p>
          )}

          <div className="flex flex-wrap gap-2">
            <button type="button" onClick={handleSave} disabled={isSaving} className={PRIMARY}>
              {isSaving ? tc('loading') : tc('save')}
            </button>
            <button
              type="button"
              onClick={() => setEditing(null)}
              disabled={isSaving}
              className={OUTLINE}
            >
              {tc('cancel')}
            </button>
          </div>
        </section>
      )}

      {/* 結構列表 */}
      {isError ? (
        <div role="alert" className="py-12 text-center space-y-3">
          <p className="text-base text-base-content">{t('structuresLoadError')}</p>
          <button type="button" onClick={() => void refetch()} className={OUTLINE}>
            {t('retry')}
          </button>
        </div>
      ) : (
        <ul className="space-y-3">
          {structures.map((bs) => {
            const isExpanded = expandedIds.has(bs.id);
            const levelsId = `structure-levels-${bs.id}`;
            const toggleExpand = () =>
              setExpandedIds((prev) => {
                const next = new Set(prev);
                if (next.has(bs.id)) next.delete(bs.id);
                else next.add(bs.id);
                return next;
              });
            return (
              <li key={bs.id} className="bg-base-200 border border-base-300">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 px-4 py-3">
                  <div className="min-w-0 flex-1">
                    <h2 className="text-lg font-bold text-base-content">
                      <button
                        type="button"
                        aria-expanded={isExpanded}
                        aria-controls={levelsId}
                        onClick={toggleExpand}
                        className="flex min-h-11 w-full min-w-0 flex-wrap items-center gap-x-2 gap-y-1 text-left hover:text-primary transition-colors"
                      >
                        <IconChevronRight
                          size={18}
                          stroke={1.5}
                          aria-hidden="true"
                          className={`shrink-0 transition-transform ${isExpanded ? 'rotate-90' : ''}`}
                        />
                        <span className="min-w-0 [overflow-wrap:anywhere]">{bs.name}</span>
                        {bs.isDefault && (
                          <span className={`${BADGE} bg-primary/10 text-primary shrink-0`}>
                            {t('defaultLabel')}
                          </span>
                        )}
                      </button>
                    </h2>
                    <p className="pl-7 text-sm text-base-content/70">
                      {t('levelCount', { count: bs.levels.length })} ·{' '}
                      {t('levelDurationLabel', {
                        duration: formatDurationLabel(bs.levelSecs, t),
                      })}
                    </p>
                  </div>
                  <div className="flex flex-wrap gap-2 shrink-0">
                    <button
                      type="button"
                      onClick={() => startEdit(bs)}
                      disabled={!!editing}
                      className={OUTLINE}
                    >
                      {tc('edit')}
                      <span className="sr-only">{bs.name}</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => void exportStructure(bs)}
                      className={OUTLINE}
                    >
                      {t('exportJson')}
                      <span className="sr-only">{bs.name}</span>
                    </button>
                    {!bs.isDefault && (
                      <button
                        type="button"
                        onClick={() => {
                          if (confirm(t('confirmDeleteStructure', { name: bs.name }))) {
                            deleteMutation.mutate(bs.id);
                          }
                        }}
                        disabled={!!editing}
                        className={DANGER_OUTLINE}
                      >
                        {tc('delete')}
                        <span className="sr-only">{bs.name}</span>
                      </button>
                    )}
                  </div>
                </div>

                {/* Level table — collapsible */}
                {isExpanded && (
                  <div className="px-4 pb-4 overflow-x-auto">
                    <StructureLevelsTable id={levelsId} structure={bs} />
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {!isError && structures.length === 0 && !editing && (
        <p className="text-center py-12 text-base-content/70">{t('noStructures')}</p>
      )}
      <CatalogPackageWorkspace />
    </div>
  );
}

/** Read-only levels of one structure. */
export function StructureLevelsTable({
  id,
  structure,
}: {
  id: string;
  structure: BlindStructureDto;
}) {
  const { t } = useTranslation('tournament');
  const amount = (value: number) => (value > 0 ? value.toLocaleString() : '-');
  return (
    <table id={id} className="relative w-full text-sm">
      <caption className="sr-only">{t('levelsCaption', { name: structure.name })}</caption>
      <thead>
        <tr className="border-b border-base-300">
          <th scope="col" className="px-2 py-2 text-left">
            #
          </th>
          <th scope="col" className="px-2 py-2 text-right">
            SB
          </th>
          <th scope="col" className="px-2 py-2 text-right">
            BB
          </th>
          <th scope="col" className="px-2 py-2 text-right">
            Ante
          </th>
          <th scope="col" className="px-2 py-2 text-right">
            BBA
          </th>
          <th scope="col" className="px-2 py-2 text-right">
            {t('levelDurationColumn')}
          </th>
        </tr>
      </thead>
      <tbody>
        {structure.levels.map((l) => (
          <tr key={l.id} className="border-b border-base-300 text-base-content">
            <td className="px-2 py-2 tabular-nums">
              {l.isBreak ? (
                <span className={`${BADGE} border border-base-300`}>{t('levelBreak')}</span>
              ) : (
                l.levelNo
              )}
            </td>
            <td className={NUM}>{l.isBreak ? '-' : l.smallBlind.toLocaleString()}</td>
            <td className={NUM}>{l.isBreak ? '-' : l.bigBlind.toLocaleString()}</td>
            <td className={NUM}>{l.isBreak ? '-' : amount(l.ante)}</td>
            <td className={NUM}>{l.isBreak ? '-' : amount(l.bba)}</td>
            <td className={NUM}>{formatDurationLabel(l.durationSecs || structure.levelSecs, t)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

const BLIND_FIELDS = [
  ['smallBlind', 'SB'],
  ['bigBlind', 'BB'],
  ['ante', 'Ante'],
  ['bba', 'BBA'],
] as const;

/** Editable level rows: every field and button is named after its level. */
export function LevelEditorTable({
  levels,
  onChange,
  onMove,
  onRemove,
}: {
  levels: LevelForm[];
  onChange: (index: number, field: keyof LevelForm, value: number | boolean | null) => void;
  onMove: (index: number, direction: 'up' | 'down') => void;
  onRemove: (index: number) => void;
}) {
  const { t } = useTranslation('tournament');
  return (
    <div className="overflow-x-auto mb-4">
      <table className="relative w-full text-sm">
        <caption className="sr-only">{t('levelsEditorCaption')}</caption>
        <thead>
          <tr className="border-b border-base-300">
            <th scope="col" className="px-2 py-2 text-left w-12">
              #
            </th>
            <th scope="col" className="px-2 py-2 text-left">
              {t('levelType')}
            </th>
            {BLIND_FIELDS.map(([, label]) => (
              <th scope="col" key={label} className="px-2 py-2 text-right">
                {label}
              </th>
            ))}
            <th scope="col" className="px-2 py-2 text-right">
              {t('durationSecs')}
            </th>
            <th scope="col" className="px-2 py-2 text-center">
              {t('levelActions')}
            </th>
          </tr>
        </thead>
        <tbody>
          {levels.map((level, idx) => (
            <tr key={idx} className="border-b border-base-300">
              <td className="px-2 py-2 tabular-nums text-base-content">{level.levelNo}</td>
              <td className="px-2 py-2">
                <span
                  className={`${BADGE} ${level.isBreak ? 'border border-base-300 text-base-content' : 'bg-primary/10 text-primary'}`}
                >
                  {level.isBreak ? t('levelBreak') : t('levelBlind')}
                </span>
              </td>
              {BLIND_FIELDS.map(([field, label]) => (
                <td key={field} className="px-2 py-2 text-right">
                  {!level.isBreak && (
                    <input
                      type="number"
                      aria-label={t('levelField', { level: level.levelNo, field: label })}
                      value={level[field]}
                      onChange={(e) => onChange(idx, field, parseInt(e.target.value, 10) || 0)}
                      className={LEVEL_FIELD}
                    />
                  )}
                </td>
              ))}
              <td className="px-2 py-2 text-right">
                <input
                  type="number"
                  aria-label={t('levelField', {
                    level: level.levelNo,
                    field: t('durationSecs'),
                  })}
                  value={level.durationSecs ?? ''}
                  onChange={(e) =>
                    onChange(
                      idx,
                      'durationSecs',
                      e.target.value ? parseInt(e.target.value, 10) : null,
                    )
                  }
                  placeholder={t('defaultDuration')}
                  className={LEVEL_FIELD}
                />
              </td>
              <td className="px-2 py-2">
                <div className="flex items-center justify-center gap-1">
                  <button
                    type="button"
                    aria-label={t('moveLevelUp', { level: level.levelNo })}
                    onClick={() => onMove(idx, 'up')}
                    disabled={idx === 0}
                    className={ICON_BUTTON}
                  >
                    <IconArrowUp size={18} stroke={1.5} aria-hidden="true" />
                  </button>
                  <button
                    type="button"
                    aria-label={t('moveLevelDown', { level: level.levelNo })}
                    onClick={() => onMove(idx, 'down')}
                    disabled={idx === levels.length - 1}
                    className={ICON_BUTTON}
                  >
                    <IconArrowDown size={18} stroke={1.5} aria-hidden="true" />
                  </button>
                  <button
                    type="button"
                    aria-label={t('removeLevel', { level: level.levelNo })}
                    onClick={() => onRemove(idx)}
                    className={`${ICON_BUTTON} text-error`}
                  >
                    <IconX size={18} stroke={1.5} aria-hidden="true" />
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * 生成預設台灣標準盲注結構（15 級 + 2 休息）
 */
function generateDefaultLevels(): LevelForm[] {
  const levels: LevelForm[] = [];
  const blinds = [
    [25, 50, 0],
    [50, 100, 0],
    [75, 150, 0],
    [100, 200, 25],
    // Break after level 4
    [150, 300, 50],
    [200, 400, 50],
    [300, 600, 75],
    [400, 800, 100],
    // Break after level 8
    [500, 1000, 100],
    [600, 1200, 200],
    [800, 1600, 200],
    [1000, 2000, 300],
    [1500, 3000, 400],
    [2000, 4000, 500],
    [3000, 6000, 1000],
  ];

  let no = 1;
  for (let i = 0; i < blinds.length; i++) {
    if (i === 4 || i === 8) {
      levels.push({
        levelNo: no++,
        isBreak: true,
        smallBlind: 0,
        bigBlind: 0,
        ante: 0,
        bba: 0,
        durationSecs: 600,
      });
    }
    const b = blinds[i]!;
    levels.push({
      levelNo: no++,
      isBreak: false,
      smallBlind: b[0] ?? 0,
      bigBlind: b[1] ?? 0,
      ante: b[2] ?? 0,
      bba: 0,
      durationSecs: null,
    });
  }
  return levels;
}
